import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  fetchPublicReport,
  fetchPublicKeywordHistory,
  publicExportCsvUrl,
  publicReportPdfUrl,
  API_BASE,
} from "@/lib/api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  ComposedChart,
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowDown,
  ArrowUp,
  ChartLineUp,
  DownloadSimple,
  FilePdf,
  Minus,
  Printer,
  Trophy,
  Warning,
  Globe,
  Calendar,
  Sparkle,
  CheckCircle,
  XCircle,
} from "@phosphor-icons/react";
import { SiOpenai, SiAnthropic, SiGooglegemini } from "react-icons/si";

const AI_META = {
  chatgpt: { label: "ChatGPT", color: "#10A37F", Icon: SiOpenai },
  claude: { label: "Claude", color: "#D97706", Icon: SiAnthropic },
  gemini: { label: "Gemini", color: "#4285F4", Icon: SiGooglegemini },
};

const GEOKATH_BLUE = "#1A6FCD";
const GEOKATH_GOLD = "#F4B400";

function PositionBadge({ position }) {
  if (position == null)
    return <span className="font-mono-data text-[var(--rt-text-muted)]">—</span>;
  let color = "text-[var(--rt-text)]";
  if (position <= 3) color = "text-[var(--rt-success)]";
  else if (position <= 10) color = "text-[#1A6FCD]";
  else if (position > 50) color = "text-[var(--rt-text-muted)]";
  return <span className={`font-mono-data text-lg font-semibold ${color}`}>{position}</span>;
}

function RankDelta({ current, previous, size = "sm" }) {
  if (current == null && previous == null)
    return <span className="text-[var(--rt-text-muted)]">—</span>;
  if (current == null)
    return <span className="text-[var(--rt-danger)] text-xs font-mono-data">LOST</span>;
  if (previous == null)
    return <span className="text-[var(--rt-text-muted)] text-xs font-mono-data">NEW</span>;
  const delta = previous - current;
  const cls = size === "lg" ? "text-base" : "text-xs";
  if (delta === 0)
    return (
      <span className={`text-[var(--rt-text-muted)] inline-flex items-center gap-1 ${cls} font-mono-data`}>
        <Minus size={size === "lg" ? 14 : 12} /> 0
      </span>
    );
  if (delta > 0)
    return (
      <span className={`text-[var(--rt-success)] inline-flex items-center gap-1 ${cls} font-mono-data`}>
        <ArrowUp size={size === "lg" ? 14 : 12} weight="bold" /> {delta}
      </span>
    );
  return (
    <span className={`text-[var(--rt-danger)] inline-flex items-center gap-1 ${cls} font-mono-data`}>
      <ArrowDown size={size === "lg" ? 14 : 12} weight="bold" /> {Math.abs(delta)}
    </span>
  );
}

function HistoryDialog({ token, keyword, open, onOpenChange }) {
  const { data, isLoading } = useQuery({
    queryKey: ["public-history", token, keyword?.id],
    queryFn: () => fetchPublicKeywordHistory(token, keyword.id),
    enabled: open && !!keyword?.id,
  });

  const snaps = data?.snapshots || [];
  const chartData = snaps.map((s) => ({
    date: new Date(s.checked_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    full_date: new Date(s.checked_at).toLocaleString(),
    position: s.position ?? 101,
    raw_position: s.position,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border border-[var(--rt-border)] bg-white max-w-3xl">
        <DialogHeader>
          <div className="label-eyebrow mb-2" style={{ color: GEOKATH_BLUE }}>/ history</div>
          <DialogTitle className="font-display text-2xl">{keyword?.keyword}</DialogTitle>
          <DialogDescription className="text-sm text-[var(--rt-text-muted)]">
            Rank position history (lower number = better)
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="h-72 animate-pulse bg-zinc-50" />
        ) : snaps.length === 0 ? (
          <div className="h-72 flex items-center justify-center text-sm text-[var(--rt-text-muted)]">
            No history yet — waiting for the next weekly check.
          </div>
        ) : (
          <div className="h-72" data-testid="public-history-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="pubRankFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GEOKATH_BLUE} stopOpacity={0.22} />
                    <stop offset="100%" stopColor={GEOKATH_BLUE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="date" stroke="#52525B" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} />
                <YAxis reversed domain={[1, 101]} ticks={[1, 10, 25, 50, 75, 100]} stroke="#52525B" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} width={36} />
                <Tooltip
                  contentStyle={{ border: "1px solid #0A0A0A", background: "#fff", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                  formatter={(v, _n, p) => [p.payload?.raw_position ?? "Not in top 100", "Position"]}
                  labelFormatter={(_, p) => p?.[0]?.payload?.full_date || ""}
                />
                <Area type="monotone" dataKey="position" stroke="none" fill="url(#pubRankFill)" />
                <Line type="monotone" dataKey="position" stroke={GEOKATH_BLUE} strokeWidth={2.5} dot={{ r: 3, fill: GEOKATH_BLUE }} activeDot={{ r: 5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DistributionBar({ buckets, total }) {
  // buckets: [{label, count, color}]
  return (
    <div>
      <div className="flex h-7 w-full border border-[var(--rt-border)] overflow-hidden">
        {buckets.map((b, i) => {
          const pct = total > 0 ? (b.count / total) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div
              key={i}
              className="h-full flex items-center justify-center text-[10px] font-mono-data font-semibold text-white transition-all"
              style={{ width: `${pct}%`, background: b.color, minWidth: pct > 0 ? "2.5%" : 0 }}
              title={`${b.label}: ${b.count} (${pct.toFixed(1)}%)`}
            >
              {pct >= 6 && b.count}
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
        {buckets.map((b, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <div className="w-2.5 h-2.5 mt-1.5 shrink-0" style={{ background: b.color }} />
            <div>
              <div className="text-xs text-[var(--rt-text-muted)]">{b.label}</div>
              <div className="font-mono-data text-base font-semibold mt-0.5">
                {b.count}
                <span className="text-[var(--rt-text-muted)] font-normal ml-1.5">
                  ({total > 0 ? ((b.count / total) * 100).toFixed(0) : 0}%)
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MoverCard({ kw, kind }) {
  const isWinner = kind === "winner";
  const delta = kw.period_delta || 0;
  return (
    <div className="border border-[var(--rt-border)] p-4 bg-white">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate" title={kw.keyword}>{kw.keyword}</div>
          <div className="text-xs text-[var(--rt-text-muted)] mt-1 font-mono-data">
            {kw.period_position ?? "—"} → {kw.last_position ?? "—"}
          </div>
        </div>
        <div
          className={`px-2 py-0.5 text-xs font-mono-data font-semibold inline-flex items-center gap-1 ${isWinner ? "bg-green-50 text-[var(--rt-success)]" : "bg-red-50 text-[var(--rt-danger)]"}`}
        >
          {isWinner ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
          {Math.abs(delta)}
        </div>
      </div>
    </div>
  );
}

export default function PublicReport() {
  const { token } = useParams();
  const [openKw, setOpenKw] = useState(null);
  const [filter, setFilter] = useState("all");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-report", token],
    queryFn: () => fetchPublicReport(token),
    retry: false,
  });

  const keywords = data?.keywords || [];
  const site = data?.website;

  // Position distribution
  const distribution = useMemo(() => {
    const top3 = keywords.filter((k) => k.last_position != null && k.last_position <= 3).length;
    const top10 = keywords.filter((k) => k.last_position != null && k.last_position > 3 && k.last_position <= 10).length;
    const top50 = keywords.filter((k) => k.last_position != null && k.last_position > 10 && k.last_position <= 50).length;
    const top100 = keywords.filter((k) => k.last_position != null && k.last_position > 50).length;
    const missing = keywords.filter((k) => k.last_position == null).length;
    return [
      { label: "Top 3", count: top3, color: "#00C853" },
      { label: "Top 10", count: top10, color: GEOKATH_BLUE },
      { label: "Top 50", count: top50, color: GEOKATH_GOLD },
      { label: "51–100", count: top100, color: "#9CA3AF" },
      { label: "Not ranking", count: missing, color: "#E4E4E7" },
    ];
  }, [keywords]);

  // Top movers - based on PERIOD delta (e.g. last 7 days)
  const { winners, losers } = useMemo(() => {
    const withDelta = keywords
      .filter((k) => k.period_delta != null && k.period_delta !== 0)
      .map((k) => ({ ...k, _delta: k.period_delta }));
    return {
      winners: [...withDelta].filter((k) => k._delta > 0).sort((a, b) => b._delta - a._delta).slice(0, 3),
      losers: [...withDelta].filter((k) => k._delta < 0).sort((a, b) => a._delta - b._delta).slice(0, 3),
    };
  }, [keywords]);

  // Visibility score: weighted score (1=position 1 worth 100, position 100 worth 1)
  const visibility = useMemo(() => {
    if (keywords.length === 0) return 0;
    const score = keywords.reduce((sum, k) => {
      const p = k.last_position;
      if (p == null) return sum;
      // Linear weight: rank 1 -> 100, rank 100 -> 1, missing -> 0
      return sum + Math.max(0, 101 - p);
    }, 0);
    const max = keywords.length * 100;
    return Math.round((score / max) * 100);
  }, [keywords]);

  const filtered = useMemo(() => {
    return keywords.filter((k) => {
      const p = k.last_position;
      if (filter === "top3") return p != null && p <= 3;
      if (filter === "top10") return p != null && p <= 10;
      if (filter === "top50") return p != null && p <= 50;
      if (filter === "missing") return p == null;
      return true;
    });
  }, [keywords, filter]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--rt-bg)]">
        <span className="label-eyebrow">Loading report…</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--rt-bg)]">
        <div className="text-center" data-testid="public-report-error">
          <div className="label-eyebrow">/ unavailable</div>
          <h1 className="font-display text-3xl mt-2">Report not found</h1>
          <p className="text-sm text-[var(--rt-text-muted)] mt-3 max-w-sm">
            This share link has been disabled or never existed.
          </p>
        </div>
      </div>
    );
  }

  const generatedOn = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const periodDays = data?.period_days || 7;
  const clientLogoUrl = site?.client_logo_url ? `${API_BASE.replace(/\/api$/, "")}${site.client_logo_url}` : null;

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-[var(--rt-bg)]" data-testid="public-report-page">
      {/* PRINT-ONLY STYLES */}
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-shadow { box-shadow: none !important; }
          .print-pagebreak { page-break-before: always; }
        }
      `}</style>

      {/* Top action bar (non-print) */}
      <header className="no-print border-b border-[var(--rt-border)] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/geokath-logo.jpg" alt="Geokath" className="h-7 w-auto object-contain" />
            <span className="label-eyebrow text-[10px] border-l border-[var(--rt-border)] pl-3">CLIENT REPORT · READ-ONLY</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={publicReportPdfUrl(token)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="public-pdf-btn"
              className="h-9 px-3 bg-[var(--rt-text)] hover:bg-zinc-800 text-white inline-flex items-center gap-2 text-sm font-medium"
            >
              <FilePdf size={14} weight="bold" /> PDF report
            </a>
            <button
              onClick={handlePrint}
              data-testid="print-report-btn"
              className="h-9 px-3 border border-[var(--rt-border)] bg-white hover:bg-[var(--rt-surface-hover)] inline-flex items-center gap-2 text-sm"
            >
              <Printer size={14} /> Print
            </button>
            <a
              href={publicExportCsvUrl(token)}
              download
              data-testid="public-csv-btn"
              className="h-9 px-3 border border-[var(--rt-border)] bg-white hover:bg-[var(--rt-surface-hover)] inline-flex items-center gap-2 text-sm"
            >
              <DownloadSimple size={14} weight="bold" /> CSV
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-10">
        {/* COVER / HERO */}
        <section className="border border-[var(--rt-border)] bg-white p-8 md:p-12 mb-8 relative overflow-hidden">
          {/* Decorative corner accent */}
          <div
            className="absolute top-0 right-0 w-32 h-32 -mr-16 -mt-16 rounded-full opacity-10"
            style={{ background: GEOKATH_GOLD }}
          />
          <div
            className="absolute bottom-0 left-0 w-40 h-40 -ml-20 -mb-20 rounded-full opacity-5"
            style={{ background: GEOKATH_BLUE }}
          />
          <div className="relative">
            <div className="flex items-center gap-5 mb-6">
              <img
                src="/brand/geokath-logo.jpg"
                alt="Geokath"
                className="h-12 md:h-14 w-auto object-contain"
              />
              {clientLogoUrl && (
                <>
                  <div className="text-[var(--rt-border)] text-3xl font-thin">×</div>
                  <img
                    src={clientLogoUrl}
                    alt={site.name}
                    className="h-12 md:h-14 w-auto object-contain"
                    data-testid="public-client-logo"
                  />
                </>
              )}
            </div>
            <div className="label-eyebrow mb-3" style={{ color: GEOKATH_BLUE }}>
              / SEO Performance Report
            </div>
            <h1
              className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95]"
              data-testid="public-site-name"
            >
              {site.name}
            </h1>
            <div className="flex items-center gap-2 mt-4 text-[var(--rt-text-muted)]">
              <Globe size={14} />
              <a
                href={`https://${site.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-mono-data hover:text-[var(--rt-primary)]"
              >
                {site.domain}
              </a>
              <span className="mx-2 text-[var(--rt-border)]">·</span>
              <Calendar size={14} />
              <span className="text-sm">{generatedOn}</span>
              <span className="mx-2 text-[var(--rt-border)]">·</span>
              <span className="text-xs label-eyebrow">Google US Desktop · Top 100</span>
            </div>
          </div>
        </section>

        {/* HERO METRIC: VISIBILITY SCORE */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="border border-[var(--rt-border)] bg-white p-6 md:col-span-1 relative overflow-hidden">
            <div className="label-eyebrow mb-2" style={{ color: GEOKATH_BLUE }}>VISIBILITY SCORE</div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-7xl leading-none">{visibility}</span>
              <span className="font-display text-2xl text-[var(--rt-text-muted)]">/100</span>
            </div>
            <p className="text-xs text-[var(--rt-text-muted)] mt-3 leading-relaxed">
              A weighted average — position 1 = 100 points, position 100 = 1 point. Higher means more keywords ranking better.
            </p>
            <div className="mt-4 h-2 bg-[#F1F1F2] relative overflow-hidden">
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: `${visibility}%`,
                  background: `linear-gradient(90deg, ${GEOKATH_BLUE} 0%, ${GEOKATH_GOLD} 100%)`,
                }}
              />
            </div>
          </div>

          <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--rt-border)] border border-[var(--rt-border)]">
            <div className="bg-white p-5">
              <div className="label-eyebrow">KEYWORDS</div>
              <div className="font-display text-3xl mt-1 leading-none">{site.keyword_count}</div>
            </div>
            <div className="bg-white p-5">
              <div className="label-eyebrow">AVG POSITION</div>
              <div className="font-display text-3xl mt-1 leading-none">{site.avg_position ?? "—"}</div>
            </div>
            <div className="bg-white p-5">
              <div className="label-eyebrow">IN TOP 3</div>
              <div className="font-display text-3xl mt-1 leading-none text-[var(--rt-success)]">{site.in_top_3}</div>
            </div>
            <div className="bg-white p-5">
              <div className="label-eyebrow">IN TOP 10</div>
              <div className="font-display text-3xl mt-1 leading-none" style={{ color: GEOKATH_BLUE }}>{site.in_top_10}</div>
            </div>
          </div>
        </section>

        {/* DISTRIBUTION */}
        <section className="border border-[var(--rt-border)] bg-white p-6 md:p-8 mb-8">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="label-eyebrow" style={{ color: GEOKATH_BLUE }}>/ position distribution</div>
              <h2 className="font-display text-2xl md:text-3xl mt-1.5 leading-tight">
                Where your keywords rank today
              </h2>
            </div>
          </div>
          <DistributionBar buckets={distribution} total={keywords.length} />
        </section>

        {/* AI VISIBILITY (only when AI keywords exist + have been checked) */}
        {data.ai_visibility && data.ai_visibility.total_keywords > 0 && (
          <section className="border border-[var(--rt-border)] bg-white p-6 md:p-8 mb-8" data-testid="public-ai-section">
            <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <Sparkle size={14} weight="fill" style={{ color: GEOKATH_GOLD }} />
                  <div className="label-eyebrow" style={{ color: GEOKATH_BLUE }}>/ ai visibility</div>
                </div>
                <h2 className="font-display text-2xl md:text-3xl mt-1 leading-tight">
                  Are you cited by AI search?
                </h2>
                <p className="text-sm text-[var(--rt-text-muted)] mt-2 max-w-2xl leading-relaxed">
                  How often the three leading AI assistants reference your domain when asked about
                  your priority topics. Higher % = stronger presence in AI-generated answers.
                </p>
              </div>
              {data.ai_visibility.last_checked_at && (
                <div className="text-[10px] label-eyebrow text-[var(--rt-text-muted)] font-mono-data">
                  CHECKED {new Date(data.ai_visibility.last_checked_at).toLocaleDateString()}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {["chatgpt", "claude", "gemini"].map((k) => {
                const meta = AI_META[k];
                const m = data.ai_visibility.per_model?.[k];
                if (!m) return null;
                const pct = m.visibility_percent ?? 0;
                const Icon = meta.Icon;
                return (
                  <div key={k} className="border border-[var(--rt-border)] p-5" data-testid={`public-ai-card-${k}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon size={16} color={meta.color} />
                        <span className="font-display text-base leading-none">{meta.label}</span>
                      </div>
                      {m.mentioned_count > 0 ? (
                        <CheckCircle size={14} weight="fill" style={{ color: meta.color }} />
                      ) : (
                        <XCircle size={14} className="text-[var(--rt-text-muted)]" />
                      )}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="font-display text-4xl leading-none">{pct}</span>
                      <span className="font-display text-lg text-[var(--rt-text-muted)]">%</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-[#F1F1F2] overflow-hidden">
                      <div
                        className="h-full transition-all duration-700"
                        style={{ width: `${pct}%`, background: meta.color }}
                      />
                    </div>
                    <div className="mt-3 pt-3 border-t border-[var(--rt-border)] grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="label-eyebrow text-[10px]">CITED IN</div>
                        <div className="font-mono-data mt-0.5">{m.mentioned_count}/{m.total_checked}</div>
                      </div>
                      <div>
                        <div className="label-eyebrow text-[10px]">AVG RANK</div>
                        <div className="font-mono-data mt-0.5">{m.avg_position ?? "—"}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* TOP MOVERS - period based */}
        {(winners.length > 0 || losers.length > 0) && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="border border-[var(--rt-border)] bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Trophy size={18} weight="duotone" style={{ color: GEOKATH_GOLD }} />
                  <div className="label-eyebrow" style={{ color: GEOKATH_BLUE }}>/ biggest wins</div>
                </div>
                <div className="text-[10px] label-eyebrow text-[var(--rt-text-muted)]">LAST {periodDays}D</div>
              </div>
              {winners.length === 0 ? (
                <div className="text-sm text-[var(--rt-text-muted)] py-3">
                  No keyword improvements this period.
                </div>
              ) : (
                <div className="space-y-2">
                  {winners.map((k) => (
                    <MoverCard key={k.id} kw={k} kind="winner" />
                  ))}
                </div>
              )}
            </div>
            <div className="border border-[var(--rt-border)] bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Warning size={18} weight="duotone" className="text-[var(--rt-danger)]" />
                  <div className="label-eyebrow text-[var(--rt-danger)]">/ needs attention</div>
                </div>
                <div className="text-[10px] label-eyebrow text-[var(--rt-text-muted)]">LAST {periodDays}D</div>
              </div>
              {losers.length === 0 ? (
                <div className="text-sm text-[var(--rt-text-muted)] py-3">
                  No keyword declines this period.
                </div>
              ) : (
                <div className="space-y-2">
                  {losers.map((k) => (
                    <MoverCard key={k.id} kw={k} kind="loser" />
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* KEYWORDS TABLE */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-eyebrow" style={{ color: GEOKATH_BLUE }}>/ all keywords</div>
              <h2 className="font-display text-2xl md:text-3xl mt-1.5 leading-tight">
                Detailed ranking breakdown
              </h2>
            </div>
            <div className="flex items-center gap-1 no-print">
              {[
                { v: "all", label: "All" },
                { v: "top3", label: "Top 3" },
                { v: "top10", label: "Top 10" },
                { v: "top50", label: "Top 50" },
                { v: "missing", label: "Not ranking" },
              ].map((f) => (
                <button
                  key={f.v}
                  data-testid={`public-filter-${f.v}`}
                  onClick={() => setFilter(f.v)}
                  className={`px-3 h-8 text-xs border ${filter === f.v ? "bg-[var(--rt-text)] text-white border-[var(--rt-text)]" : "bg-white border-[var(--rt-border)] text-[var(--rt-text)] hover:bg-[var(--rt-surface-hover)]"} transition-colors`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border border-[var(--rt-border)] bg-white">
            <div className="grid grid-cols-12 gap-4 px-4 py-3 label-eyebrow border-b border-[var(--rt-border)] bg-[#FAFAFA]">
              <div className="col-span-6">Keyword</div>
              <div className="col-span-1 text-right">Pos.</div>
              <div className="col-span-1 text-right" title="Change vs the prior period">{periodDays}d Δ</div>
              <div className="col-span-3 truncate">Matched URL</div>
              <div className="col-span-1 text-right no-print">History</div>
            </div>
            {filtered.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-[var(--rt-text-muted)]">No keywords match this filter.</div>
            ) : (
              filtered.map((k) => (
                <div
                  key={k.id}
                  data-testid={`public-kw-row-${k.id}`}
                  className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-[var(--rt-border)] last:border-b-0 rt-row-zebra items-center"
                >
                  <div className="col-span-6 text-sm truncate">{k.keyword}</div>
                  <div className="col-span-1 text-right"><PositionBadge position={k.last_position} /></div>
                  <div className="col-span-1 text-right">
                    <RankDelta current={k.last_position} previous={k.period_position} />
                  </div>
                  <div className="col-span-3 truncate">
                    {k.last_matched_url ? (
                      <a href={k.last_matched_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--rt-text-muted)] hover:text-[var(--rt-primary)] font-mono-data truncate inline-block max-w-full">
                        {k.last_matched_url.replace(/^https?:\/\//, "")}
                      </a>
                    ) : (
                      <span className="text-xs text-[var(--rt-text-muted)]">—</span>
                    )}
                  </div>
                  <div className="col-span-1 flex items-center justify-end no-print">
                    <button
                      onClick={() => setOpenKw(k)}
                      data-testid={`public-view-history-${k.id}`}
                      className="p-1.5 text-[var(--rt-text-muted)] hover:text-[var(--rt-primary)] hover:bg-[var(--rt-surface-hover)] transition-colors"
                      title="View history"
                    >
                      <ChartLineUp size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* FOOTER */}
        <footer className="mt-12 pt-8 border-t-2 border-[var(--rt-text)] grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <img src="/brand/geokath-logo.jpg" alt="Geokath" className="h-10 w-auto object-contain mb-3" />
            <p className="text-sm text-[var(--rt-text-muted)] leading-relaxed max-w-md">
              This report was prepared by <span className="font-semibold text-[var(--rt-text)]">Geokath Marketing</span>.
              Rankings are sourced live from Google US desktop search results, top 100 organic positions,
              refreshed weekly.
            </p>
          </div>
          <div className="text-xs space-y-2">
            <div className="label-eyebrow" style={{ color: GEOKATH_BLUE }}>/ contact</div>
            <a href="https://geokathmarketing.com" target="_blank" rel="noopener noreferrer" className="block hover:text-[var(--rt-primary)] font-mono-data">
              geokathmarketing.com
            </a>
            <div className="text-[var(--rt-text-muted)] mt-3 pt-3 border-t border-[var(--rt-border)] font-mono-data text-[10px] uppercase tracking-wider">
              Report ID · {token.substring(0, 8).toUpperCase()}
            </div>
          </div>
        </footer>
      </main>

      {openKw && (
        <HistoryDialog
          token={token}
          keyword={openKw}
          open={!!openKw}
          onOpenChange={(o) => !o && setOpenKw(null)}
        />
      )}
    </div>
  );
}
